/** Aspect-aware card layout engine. Positions are normalized center x/y + width/height. */

import { aspectRatioValue, clampCarouselParams } from './template';

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

function safeBox(aspect) {
  const portrait = aspect <= 0.8;
  const square = aspect > 0.8 && aspect < 1.3;
  if (portrait) {
    return { left: 0.07, right: 0.93, top: 0.58, bottom: 0.93 };
  }
  if (square) {
    return { left: 0.08, right: 0.92, top: 0.62, bottom: 0.92 };
  }
  return { left: 0.08, right: 0.92, top: 0.58, bottom: 0.9 };
}

function cardSize(count, layout, aspect, scale, gap) {
  const portrait = aspect <= 0.8;
  const baseW = portrait ? 0.28 : aspect >= 1.3 ? 0.22 : 0.26;
  const baseH = portrait ? 0.3 : 0.32;
  let width = baseW * scale;
  let height = baseH * scale;
  if (layout === 'horizontal') {
    const avail = 0.86;
    width = clamp((avail - gap * Math.max(0, count - 1)) / Math.max(1, count), 0.14, 0.34) * scale;
    height = clamp(width * 1.15, 0.2, 0.34);
  }
  if (layout === 'vertical') {
    height = clamp((0.34 - gap * Math.max(0, count - 1)) / Math.max(1, count), 0.1, 0.22) * scale;
    width = clamp(height * 1.5, 0.42, 0.72);
  }
  if (layout === 'grid') {
    width = clamp(0.38 * scale, 0.22, 0.42);
    height = clamp(0.2 * scale, 0.14, 0.26);
  }
  if (layout === 'center') {
    width = clamp(0.3 * scale, 0.2, 0.38);
    height = clamp(0.26 * scale, 0.16, 0.32);
  }
  if (layout === 'offset') {
    width = clamp(0.34 * scale, 0.22, 0.4);
    height = clamp(0.22 * scale, 0.14, 0.28);
  }
  return { width, height };
}

export function layoutCardFrames({
  count,
  layout = 'horizontal',
  aspectRatio = '9:16',
  gap,
  scale,
} = {}) {
  const n = Math.max(1, Math.min(20, Number(count) || 1));
  const params = clampCarouselParams({ layout, gap, scale });
  const aspect = aspectRatioValue(aspectRatio);
  const box = safeBox(aspect);
  const { width, height } = cardSize(n, params.layout, aspect, params.scale, params.gap);
  const frames = [];

  if (params.layout === 'vertical') {
    const totalH = n * height + (n - 1) * params.gap;
    let y = box.bottom - totalH + height / 2;
    const x = 0.5;
    for (let i = 0; i < n; i += 1) {
      frames.push({ x, y: clamp(y, box.top + height / 2, box.bottom - height / 2), width, height, rotation: 0 });
      y += height + params.gap;
    }
    return frames;
  }

  if (params.layout === 'offset') {
    const xs = [0.38, 0.5, 0.62];
    const totalH = n * (height * 0.72) + (n - 1) * params.gap * 0.4;
    let y = box.bottom - totalH + height / 2;
    for (let i = 0; i < n; i += 1) {
      frames.push({
        x: xs[i % xs.length],
        y: clamp(y, box.top + height / 2, box.bottom - height / 2),
        width,
        height,
        rotation: (i % 2 === 0 ? -1 : 1) * 3,
      });
      y += height * 0.72 + params.gap * 0.4;
    }
    return frames;
  }

  if (params.layout === 'grid') {
    const cols = n <= 3 ? n : 2;
    const rows = Math.ceil(n / cols);
    const gridW = cols * width + (cols - 1) * params.gap;
    const gridH = rows * height + (rows - 1) * params.gap;
    const x0 = 0.5 - gridW / 2 + width / 2;
    const y0 = box.bottom - gridH + height / 2;
    for (let i = 0; i < n; i += 1) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      frames.push({
        x: clamp(x0 + col * (width + params.gap), box.left + width / 2, box.right - width / 2),
        y: clamp(y0 + row * (height + params.gap), box.top + height / 2, box.bottom - height / 2),
        width,
        height,
        rotation: 0,
      });
    }
    return frames;
  }

  if (params.layout === 'center') {
    if (n === 1) {
      return [{ x: 0.5, y: (box.top + box.bottom) / 2, width: width * 1.15, height: height * 1.15, rotation: 0 }];
    }
    frames.push({ x: 0.5, y: box.top + height / 2 + 0.02, width, height, rotation: 0 });
    const rest = n - 1;
    const rowW = rest * width * 0.92 + (rest - 1) * params.gap;
    let x = 0.5 - rowW / 2 + (width * 0.92) / 2;
    for (let i = 1; i < n; i += 1) {
      frames.push({
        x: clamp(x, box.left + width / 2, box.right - width / 2),
        y: box.bottom - height / 2,
        width: width * 0.92,
        height: height * 0.92,
        rotation: 0,
      });
      x += width * 0.92 + params.gap;
    }
    return frames;
  }

  const totalW = n * width + (n - 1) * params.gap;
  let x = 0.5 - totalW / 2 + width / 2;
  const y = box.bottom - height / 2;
  for (let i = 0; i < n; i += 1) {
    frames.push({
      x: clamp(x, box.left + width / 2, box.right - width / 2),
      y,
      width,
      height,
      rotation: 0,
    });
    x += width + params.gap;
  }
  return frames;
}
